import { useEffect, useState, useMemo } from "react";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Save, FileText, Download, ShieldCheck, RefreshCw, Lock, CheckCheck } from "lucide-react";

const FORMAT_NO = "QC008/F01-00";
// Rows of the certificate that are a pass/fail statement (Description, Text matter ... Type of Ink).
const STATEMENT_ROWS = [0, 7, 8, 9, 10, 11, 12];

export default function COAEditor({ job }) {
  const [coa, setCoa] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/jobs/${job.id}/coa`)
      .then(({ data }) => setCoa(data))
      .catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [job?.id]);

  const set = (patch) => setCoa((c) => ({ ...c, ...patch }));
  const setTest = (idx, patch) => setCoa((c) => ({
    ...c, tests: (c.tests || []).map((t, i) => (i === idx ? { ...t, ...patch } : t)),
  }));
  // The pass/fail statement rows of the certificate; blank results are filled with "Complies".
  const fillComplies = () => setCoa((c) => ({
    ...c, tests: (c.tests || []).map((t, i) => (STATEMENT_ROWS.includes(i) && !(t.result || "").trim() ? { ...t, result: "Complies" } : t)),
  }));

  const isFinalized = coa?.status === "finalized";

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/jobs/${job.id}/coa`, coa);
      setCoa(data);
      toast.success("COA saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const redraft = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${job.id}/coa/redraft`);
      setCoa(data);
      toast.success("Re-drafted from job data");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const finalize = async () => {
    if (!window.confirm("Finalize this COA? It will be locked against auto re-draft.")) return;
    setBusy(true);
    try {
      await save();
      const { data } = await api.post(`/jobs/${job.id}/coa/finalize`);
      setCoa(data);
      toast.success("COA finalized");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    setBusy(true);
    try {
      await save();
      const token = localStorage.getItem("erp_token");
      const res = await fetch(`${API_BASE}/jobs/${job.id}/coa/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `COA_${job.mpwo_number || job.id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) { toast.error(`PDF error: ${e.message}`); }
    finally { setBusy(false); }
  };

  const previewPdf = async () => {
    await save();
    const token = localStorage.getItem("erp_token");
    const res = await fetch(`${API_BASE}/jobs/${job.id}/coa/pdf`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    window.open(URL.createObjectURL(blob), "_blank");
  };

  if (!coa) return <div className="text-neutral-500 p-8">Loading COA…</div>;

  return (
    <div className="space-y-4" data-testid="coa-editor">
      {/* Status bar */}
      <div className="flex items-center justify-between border border-neutral-200 bg-white px-4 py-2.5">
        <div className="flex items-center gap-3">
          <span className={`px-2 py-1 rounded-sm text-[10px] uppercase tracking-widest font-bold border ${isFinalized ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-amber-100 text-amber-800 border-amber-300"}`}>
            {isFinalized ? <><Lock size={11} className="inline mr-1" />Finalized</> : "Draft"}
          </span>
          <span className="text-xs text-neutral-500">
            Certificate of Analysis, format {FORMAT_NO}. Drafted from the job and its stages; fill the results and finalize when QC is done.
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="label-caps text-[10px]">Material status</span>
          <select
            data-testid="coa-material-status"
            disabled={isFinalized}
            value={coa.material_status || "Approved"}
            onChange={(e) => set({ material_status: e.target.value })}
            className={`px-2 py-1 border rounded-sm text-xs font-bold ${coa.material_status === "Approved" ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-rose-100 text-rose-800 border-rose-300"}`}
          >
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
            <option value="On Hold">On Hold</option>
          </select>
        </div>
      </div>

      {/* Customer / item block, as at the top of the certificate */}
      <div className="border border-neutral-200 bg-white p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Fld label="Customer Name" value={coa.customer_name} onChange={(v) => set({ customer_name: v })} testid="coa-customer" disabled={isFinalized} />
          <Fld label="W/O Batch No." value={coa.batch_no} onChange={(v) => set({ batch_no: v })} testid="coa-batch" disabled={isFinalized} />
          <Fld label="Item Code" value={coa.item_code} onChange={(v) => set({ item_code: v })} testid="coa-item-code" disabled={isFinalized} />
          <Fld label="Quantity (No's)" type="number" value={coa.quantity} onChange={(v) => set({ quantity: v })} testid="coa-qty" disabled={isFinalized} />
          <div className="col-span-2">
            <Fld label="Item Name" value={coa.item_name} onChange={(v) => set({ item_name: v })} testid="coa-product" disabled={isFinalized} />
          </div>
          <Fld label="Specification No" value={coa.spec_no} onChange={(v) => set({ spec_no: v })} testid="coa-spec-no" disabled={isFinalized} />
          <Fld label="COA No." value={coa.coa_no} onChange={(v) => set({ coa_no: v })} testid="coa-no" disabled={isFinalized} />
          <Fld label="Invoice No." value={coa.invoice_no} onChange={(v) => set({ invoice_no: v })} testid="coa-invoice-no" disabled={isFinalized} />
          <Fld label="Invoice Date" type="date" value={coa.invoice_date} onChange={(v) => set({ invoice_date: v })} testid="coa-invoice-date" disabled={isFinalized} />
          <Fld label="Mfg. Date" type="date" value={coa.mfg_date} onChange={(v) => set({ mfg_date: v })} testid="coa-mfg" disabled={isFinalized} />
          <Fld label="Issue Date" type="date" value={coa.issue_date} onChange={(v) => set({ issue_date: v })} testid="coa-issue-date" disabled={isFinalized} />
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        {!isFinalized && (
          <Button data-testid="coa-redraft" onClick={redraft} variant="outline" className="rounded-sm gap-1.5">
            <RefreshCw size={13} /> Re-draft from Job
          </Button>
        )}
        {!isFinalized && (
          <Button data-testid="coa-fill-complies" onClick={fillComplies} variant="outline" className="rounded-sm gap-1.5">
            <CheckCheck size={13} /> Fill blank statements with "Complies"
          </Button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button data-testid="coa-save" onClick={save} disabled={busy || isFinalized} variant="outline" className="rounded-sm gap-1.5">
            <Save size={13} /> {busy ? "Saving…" : "Save Draft"}
          </Button>
          <Button data-testid="coa-preview" onClick={previewPdf} disabled={busy} variant="outline" className="rounded-sm gap-1.5">
            <FileText size={13} /> Preview PDF
          </Button>
          {!isFinalized && (
            <Button data-testid="coa-finalize" onClick={finalize} disabled={busy} variant="outline" className="rounded-sm gap-1.5 border-emerald-400 text-emerald-700 hover:bg-emerald-50">
              <ShieldCheck size={13} /> Finalize COA
            </Button>
          )}
          <Button data-testid="coa-download" onClick={downloadPdf} disabled={busy} className="rounded-sm gap-1.5 bg-[#FF5A00] hover:bg-[#e04f00]">
            <Download size={13} /> Save &amp; Download PDF
          </Button>
        </div>
      </div>

      {/* Technical specification */}
      <div className="border border-neutral-200 bg-white">
        <div className="px-4 py-2.5 bg-neutral-50 border-b border-neutral-200 font-semibold text-neutral-900">Technical Specification</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="coa-table-tests">
            <thead className="bg-neutral-50 label-caps text-[10px]">
              <tr>
                <th className="text-center px-2 py-2 w-10">S.No.</th>
                <th className="text-left px-2 py-2 w-56">Test Parameter</th>
                <th className="text-left px-2 py-2">Specification</th>
                <th className="text-left px-2 py-2 w-56">Result</th>
              </tr>
            </thead>
            <tbody>
              {(coa.tests || []).map((t, i) => (
                <tr key={i} className="border-t border-neutral-100 align-top">
                  <td className="px-2 py-2 text-center font-mono-num text-neutral-500 text-xs">{i + 1}</td>
                  <td className="px-2 py-1"><InlineIn multi value={t.parameter} onChange={(v) => setTest(i, { parameter: v })} testid={`coa-tests-${i}-param`} disabled={isFinalized} /></td>
                  <td className="px-2 py-1"><InlineIn multi value={t.specification} onChange={(v) => setTest(i, { specification: v })} testid={`coa-tests-${i}-spec`} disabled={isFinalized} /></td>
                  <td className="px-2 py-1"><InlineIn multi={i >= 13} value={t.result} onChange={(v) => setTest(i, { result: v })} testid={`coa-tests-${i}-result`} disabled={isFinalized} placeholder={i >= 13 ? "" : "Complies / measured value"} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Signatures */}
      <div className="border border-neutral-200 bg-white p-4">
        <div className="grid grid-cols-2 gap-3">
          <Fld label="Tested & Prepared By" value={coa.tested_by} onChange={(v) => set({ tested_by: v })} testid="coa-tested-by" disabled={isFinalized} />
          <Fld label="Reviewed & Approved By" value={coa.reviewed_by} onChange={(v) => set({ reviewed_by: v })} testid="coa-reviewed-by" disabled={isFinalized} />
        </div>
      </div>
    </div>
  );
}

function Fld({ label, value, onChange, testid, type = "text", disabled }) {
  return (
    <div>
      <label className="label-caps block mb-1 text-[10px]">{label}</label>
      <Input data-testid={testid} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        disabled={disabled} className="rounded-sm h-8 text-sm" />
    </div>
  );
}

function InlineIn({ value, onChange, testid, disabled, placeholder, multi }) {
  const cls = "w-full border border-transparent hover:border-neutral-200 focus:border-[#FF5A00] focus:outline-none px-2 py-1 text-sm rounded-sm disabled:bg-neutral-50 disabled:text-neutral-500";
  if (multi) {
    const rows = Math.max(1, (value || "").split("\n").length, Math.ceil((value || "").length / 70));
    return <textarea data-testid={testid} value={value == null ? "" : value} rows={rows} onChange={(e) => onChange(e.target.value)}
      disabled={disabled} placeholder={placeholder} className={`${cls} resize-none`} />;
  }
  return (
    <input
      data-testid={testid}
      value={value == null ? "" : value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      placeholder={placeholder}
      className={cls}
    />
  );
}
